import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../../utils";
import { RootState } from "../../../../redux/store";
import {
  CardsAPI,
  CardsListApiType,
  CardsListType,
  MultipleCardsOrderType,
  UpdateStatusType,
  UpdateCardStatusType,
  QrCodesUrl,
  QrCodeInfo,
  GetShipmentLinkPayload,
} from "./cardsAPI";
import axios from "axios";

export interface CardsState {
  createCardStatus: QueryStatus;
  cardsListStatus: QueryStatus;
  cardsList: CardsListType[];
  cardUpdatedStatus: QueryStatus;
  orderMultipleCardsStatus: QueryStatus;
  companyAddress: string;
  getAddressStatus: QueryStatus;
  newCardsListOrdered: Omit<MultipleCardsOrderType, "deliveryAddress">;
  ruleUpdatedStatus: QueryStatus;
  ruleUpdated: UpdateStatusType | null;
  downloadedQrCodeStatus: QueryStatus;
  qrCodesUrl: QrCodesUrl[];
  qrCodesUrlStatus: QueryStatus;
  getShipmentLink: string;
  getShipmentLinkStatus: QueryStatus;
}

const initialState: CardsState = {
  createCardStatus: "idle",
  cardsListStatus: "idle",
  cardsList: [],
  cardUpdatedStatus: "idle",
  orderMultipleCardsStatus: "idle",
  companyAddress: "",
  getAddressStatus: "idle",
  newCardsListOrdered: {
    vehicles: [],
    rule: "",
  },
  ruleUpdatedStatus: "idle",
  ruleUpdated: null,
  downloadedQrCodeStatus: "idle",
  qrCodesUrl: [],
  qrCodesUrlStatus: "idle",
  getShipmentLink: "",
  getShipmentLinkStatus: "idle",
};

export const cardsAsync = createAsyncThunk(
  "cards/call",
  async (payload: CardsAPI, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`card`, payload);
  },
);

export const cardsListAsync = createAsyncThunk(
  "cardsList/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<Array<CardsListApiType>>(
      `cards/company/${companyUuid}`,
    );
    const cardsList = response.data.map((payload) => ({
      ...payload,
      monthlyExpenses: Number((payload.monthlyExpenses / 100).toFixed(2)),
      monthlyAmountlimit: Number((payload.monthlyAmountlimit / 100).toFixed(2)),
      date: payload.createdAt,
      vehicleReference: payload.vehicleReference?.toLocaleUpperCase() ?? null,
      driverName: [payload.driverFirstName, payload.driverLastName]
        .filter((d) => d)
        .join(" "),
      vehicleInfo: [
        payload.registrationNumber,
        payload.vehicleReference?.toLocaleUpperCase(),
      ]
        .filter((d) => d)
        .join(" / "),
      qrCodesIdentifier: payload.qrCodes
        ? payload.qrCodes.map((qr) => qr.identifier).join(" / ")
        : "",
      shipmentLink: null,
    }));
    return cardsList;
  },
);

export const createCardsForDepartmentAsync = createAsyncThunk(
  "cardsForDepartmentUser/createCard",
  async (payload: CardsAPI, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;
    if (!department) {
      throw new Error("Department not selected");
    }
    const axios = authAxios({ departmentUuid: department });
    await axios.post(`departments/card`, payload);
  },
);

export const cardsListOfADepartmentAsync = createAsyncThunk(
  "cardsForDepartmentUser/listCardsByDepartment",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid: departmentUuid });

    const response = await axios.get<Array<CardsListApiType>>(
      `departments/cards/department/${departmentUuid}`,
    );

    const cardsList = response.data.map((payload) => ({
      ...payload,
      monthlyExpenses: Number((payload.monthlyExpenses / 100).toFixed(2)),
      monthlyAmountlimit: Number((payload.monthlyAmountlimit / 100).toFixed(2)),
      date: payload.createdAt,
      vehicleReference: payload.vehicleReference?.toLocaleUpperCase() ?? null,
      driverName: [payload.driverFirstName, payload.driverLastName]
        .filter((d) => d)
        .join(" "),
      vehicleInfo: [
        payload.registrationNumber,
        payload.vehicleReference?.toLocaleUpperCase(),
      ]
        .filter((d) => d)
        .join(" / "),
      qrCodesIdentifier: payload.qrCodes
        ? payload.qrCodes.map((qr) => qr.identifier).join(" / ")
        : "",
      shipmentLink: null,
    }));
    return cardsList;
  },
);

export const getShipmentLinkAsync = createAsyncThunk(
  "cardsForDepartmentUser/getShipmentLink",
  async (payload: GetShipmentLinkPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ link: string }>(
      `/card/shipment_link/${payload.shipmentUuid}`,
    );
    void thunkAPI.dispatch(
      cardsSlice.actions.setShipmentLink({
        cardUuid: payload.cardUuid,
        shipmentLink: response.data.link,
      }),
    );
  },
);

export const updateCardStatusForDepartmentAsync = createAsyncThunk(
  "cardsForDepartmentUser/updateCardStatus",
  async (payload: UpdateCardStatusType, thunkAPI) => {
    const department = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;

    if (!department) {
      throw new Error("Department not selected");
    }

    const axios = authAxios({ departmentUuid: department });
    await axios.post(
      `departments/card/${payload.cardUuid}/update_card_status`,
      {
        status: payload.status,
      },
    );
    return { status: payload.status };
  },
);

export const updateCardStatusAsync = createAsyncThunk(
  "updateCardStatus/call",
  async (payload: UpdateCardStatusType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/card/${payload.cardUuid}/update_card_status`, {
      status: payload.status,
    });
    return { status: payload.status };
  },
);

export const updateVehicleRuleAsync = createAsyncThunk(
  "updateVehicleRule/call",
  async (payload: { vehicleUuid: string; ruleUuid: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/vehicle/${payload.vehicleUuid}/update_rule`, {
      rule: payload.ruleUuid,
    });
  },
);

export const orderMultipleCardsAsync = createAsyncThunk(
  "orderMultipleCards/call",
  async (payload: MultipleCardsOrderType, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/cards/bulk_order`, payload);
    return { vehicles: payload.vehicles, rule: payload.rule };
  },
);

export const addressAsync = createAsyncThunk(
  "address/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<{ address: string }>(
      `address/company/${companyUuid}`,
    );
    return response.data.address;
  },
);

export const getQrCodePresignedUrlAsync = createAsyncThunk(
  "qrCodeDownload/call",
  async (qrCode: QrCodeInfo, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axioAuth = authAxios({ companyUuid: company?.uuid });
    const responseGet = await axioAuth.get<{ presignedUrl: string }>(
      `/qr_code/${qrCode.archiveFileName}/export`,
    );
    const response = await axios({
      url: responseGet.data.presignedUrl,
      method: "GET",
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );
    void thunkAPI.dispatch(
      cardsSlice.actions.setUrlForAQrCode({
        archiveFileName: qrCode.archiveFileName,
        identifier: qrCode.identifier,
        url,
        lastScanDate: qrCode.lastScanDate,
      }),
    );
  },
);

export const getQrCodePresignedUrlForDepartmentAsync = createAsyncThunk(
  "getQrCodePresignedUrlForDepartmentAsync/call",
  async (qrCode: QrCodeInfo, thunkAPI) => {
    const departmentUuid = (thunkAPI.getState() as RootState)
      .departmentsForDepartmentUser.currentDepartmentUuid;

    if (!departmentUuid) {
      throw new Error("Department not selected");
    }
    const axioAuth = authAxios({ departmentUuid });
    const responseGet = await axioAuth.get<{ presignedUrl: string }>(
      `departments/qr_code/${qrCode.archiveFileName}/export`,
    );
    const response = await axios({
      url: responseGet.data.presignedUrl,
      method: "GET",
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );
    void thunkAPI.dispatch(
      cardsSlice.actions.setUrlForAQrCode({
        archiveFileName: qrCode.archiveFileName,
        identifier: qrCode.identifier,
        url,
        lastScanDate: qrCode.lastScanDate,
      }),
    );
  },
);

export const qrCodeDownloadAsync = createAsyncThunk(
  "qrCodeDownloadAsync/call",
  (payload: { url: string; fileName: string }) => {
    if (payload.url) {
      const a = document.createElement("a");
      a.download = payload.fileName || "yourDocument.pdf";
      a.href = payload.url;
      a.click();
    }
  },
);

export const cardsSlice = createSlice({
  name: "cards",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetCardsListStatus(state) {
      state.cardsListStatus = "idle";
    },
    resetCardUpdatedStatus(state) {
      state.cardUpdatedStatus = "idle";
    },
    resetOrderMultipleCardsStatus(state) {
      state.orderMultipleCardsStatus = "idle";
    },
    resetRuleUpdatedStatus(state) {
      state.ruleUpdatedStatus = "idle";
    },
    resetCardCreationStatus(state) {
      state.createCardStatus = "idle";
    },
    setUrlForAQrCode(
      state,
      action: PayloadAction<{
        archiveFileName: string;
        url: string;
        identifier: string;
        lastScanDate: string | null;
      }>,
    ) {
      state.qrCodesUrl?.push(action.payload);
    },
    resetUrlForQrCodes(state) {
      state.qrCodesUrl = [];
      state.qrCodesUrlStatus = "idle";
    },
    setShipmentLink(
      state,
      action: PayloadAction<{ cardUuid: string; shipmentLink: string }>,
    ) {
      state.cardsList = state.cardsList.map((card) =>
        card.uuid === action.payload.cardUuid
          ? { ...card, shipmentLink: action.payload.shipmentLink }
          : card,
      );
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(cardsAsync.pending, (state) => {
        state.createCardStatus = "processing";
      })
      .addCase(cardsAsync.fulfilled, (state) => {
        state.createCardStatus = "success";
        state.cardsListStatus = "idle";
      })
      .addCase(cardsAsync.rejected, (state) => {
        state.createCardStatus = "failed";
      })
      .addCase(cardsListAsync.pending, (state) => {
        state.cardsListStatus = "processing";
      })
      .addCase(cardsListAsync.fulfilled, (state, action) => {
        state.cardsList = action.payload;
        state.cardsListStatus = "success";
      })
      .addCase(cardsListAsync.rejected, (state) => {
        state.cardsListStatus = "failed";
      })
      .addCase(createCardsForDepartmentAsync.pending, (state) => {
        state.createCardStatus = "processing";
      })
      .addCase(createCardsForDepartmentAsync.fulfilled, (state) => {
        state.createCardStatus = "success";
      })
      .addCase(createCardsForDepartmentAsync.rejected, (state) => {
        state.createCardStatus = "failed";
      })
      .addCase(cardsListOfADepartmentAsync.pending, (state) => {
        state.cardsListStatus = "processing";
      })
      .addCase(cardsListOfADepartmentAsync.fulfilled, (state, action) => {
        state.cardsList = action.payload;
        state.cardsListStatus = "success";
      })
      .addCase(cardsListOfADepartmentAsync.rejected, (state) => {
        state.cardsListStatus = "failed";
      })
      .addCase(updateCardStatusForDepartmentAsync.pending, (state) => {
        state.cardUpdatedStatus = "processing";
      })
      .addCase(
        updateCardStatusForDepartmentAsync.fulfilled,
        (state, action) => {
          state.cardUpdatedStatus = "success";
          state.cardsListStatus = "idle";
          state.ruleUpdated = action.payload.status;
        },
      )
      .addCase(updateCardStatusForDepartmentAsync.rejected, (state) => {
        state.cardUpdatedStatus = "failed";
      })
      .addCase(updateCardStatusAsync.pending, (state) => {
        state.cardUpdatedStatus = "processing";
      })
      .addCase(updateCardStatusAsync.fulfilled, (state, action) => {
        state.cardUpdatedStatus = "success";
        state.cardsListStatus = "idle";
        state.ruleUpdated = action.payload.status;
      })
      .addCase(updateCardStatusAsync.rejected, (state) => {
        state.cardUpdatedStatus = "failed";
      })

      .addCase(orderMultipleCardsAsync.pending, (state) => {
        state.orderMultipleCardsStatus = "processing";
      })
      .addCase(orderMultipleCardsAsync.fulfilled, (state, action) => {
        state.orderMultipleCardsStatus = "success";
        state.cardsListStatus = "idle";
        state.newCardsListOrdered = action.payload;
      })
      .addCase(orderMultipleCardsAsync.rejected, (state) => {
        state.orderMultipleCardsStatus = "failed";
      })
      .addCase(addressAsync.pending, (state) => {
        state.getAddressStatus = "processing";
      })
      .addCase(addressAsync.fulfilled, (state, action) => {
        state.companyAddress = action.payload;
        state.getAddressStatus = "success";
      })
      .addCase(addressAsync.rejected, (state) => {
        state.getAddressStatus = "failed";
      })
      .addCase(updateVehicleRuleAsync.pending, (state) => {
        state.ruleUpdatedStatus = "processing";
      })
      .addCase(updateVehicleRuleAsync.fulfilled, (state) => {
        state.ruleUpdatedStatus = "success";
        state.cardsListStatus = "idle";
      })
      .addCase(updateVehicleRuleAsync.rejected, (state) => {
        state.ruleUpdatedStatus = "failed";
      })
      .addCase(qrCodeDownloadAsync.pending, (state) => {
        state.downloadedQrCodeStatus = "processing";
      })
      .addCase(qrCodeDownloadAsync.fulfilled, (state) => {
        state.downloadedQrCodeStatus = "success";
      })
      .addCase(qrCodeDownloadAsync.rejected, (state) => {
        state.downloadedQrCodeStatus = "failed";
      })
      .addCase(getQrCodePresignedUrlAsync.pending, (state) => {
        state.qrCodesUrlStatus = "processing";
      })
      .addCase(getQrCodePresignedUrlAsync.fulfilled, (state) => {
        state.qrCodesUrlStatus = "success";
      })
      .addCase(getQrCodePresignedUrlAsync.rejected, (state) => {
        state.qrCodesUrlStatus = "failed";
      })
      .addCase(getQrCodePresignedUrlForDepartmentAsync.pending, (state) => {
        state.qrCodesUrlStatus = "processing";
      })
      .addCase(getQrCodePresignedUrlForDepartmentAsync.fulfilled, (state) => {
        state.qrCodesUrlStatus = "success";
      })
      .addCase(getQrCodePresignedUrlForDepartmentAsync.rejected, (state) => {
        state.qrCodesUrlStatus = "failed";
      })
      .addCase(getShipmentLinkAsync.pending, (state) => {
        state.getShipmentLinkStatus = "processing";
      })
      .addCase(getShipmentLinkAsync.fulfilled, (state) => {
        state.getShipmentLinkStatus = "success";
      })
      .addCase(getShipmentLinkAsync.rejected, (state) => {
        state.getShipmentLinkStatus = "failed";
      });
  },
});

export const selectCardCreationStatus = (state: RootState) =>
  state.cards.createCardStatus;

export const selectCardsList = (state: RootState) => state.cards.cardsList;

export const selectCardsListStatus = (state: RootState) =>
  state.cards.cardsListStatus;

export const selectCardUpdatedStatus = (state: RootState) =>
  state.cards.cardUpdatedStatus;

export const selectOrderMultipleCardsStatus = (state: RootState) =>
  state.cards.orderMultipleCardsStatus;

export const selectCompanyAddress = (state: RootState) =>
  state.cards.companyAddress;

export const selectNewCardsList = (state: RootState) =>
  state.cards.newCardsListOrdered;

export const selectRuleUpdatedStatus = (state: RootState) =>
  state.cards.ruleUpdatedStatus;

export const selectRuleUpdated = (state: RootState) => state.cards.ruleUpdated;

export const selectDownloadedQrCodeStatus = (state: RootState) =>
  state.cards.downloadedQrCodeStatus;

export const selectQrCodesUrl = (state: RootState) => state.cards.qrCodesUrl;

export const selectQrCodesUrlStatus = (state: RootState) =>
  state.cards.qrCodesUrlStatus;

export const selectGetShipmentLink = (state: RootState) =>
  state.cards.getShipmentLink;

export const selectGetAddressStatus = (state: RootState) =>
  state.cards.getAddressStatus;

export default cardsSlice.reducer;
